// reports/NaturesCountReport.jsx
//
// Complaint count per complaint nature, for the range shown on the Natures
// Count page. Columns: # | Complaint Nature | Count | Share (bar + %), with a
// total row at the end.
//
// Same visual language as ClosedComplaintsReport.jsx (F&W logo header, 1px
// black rule + page-number footer, A4 padding).

import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import './pdfFonts';

const SKY = { 50: '#f0f9ff', 100: '#e0f2fe', 200: '#bae6fd', 800: '#075985', 900: '#0c4a6e' };
const BAR = '#45b369';

const COL = { index: '8%', name: '47%', count: '13%', share: '32%' };

const styles = StyleSheet.create({
  page: {
    fontFamily: 'NotoNaskhArabic',
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    paddingTop: 30,
    paddingBottom: 50,
    paddingLeft: 20,
    paddingRight: 20,
  },
  headerBlock: { minHeight: 90 },
  dividerTop: { marginBottom: 20, borderBottomWidth: 1, borderBottomColor: '#ccc', marginVertical: 10 },
  heading: { fontSize: 20, marginBottom: 0, marginTop: 10, textAlign: 'center', fontWeight: 'bold', color: SKY[800] },
  subHeading: { fontSize: 11, textAlign: 'center', marginTop: 4, marginBottom: 12, color: '#555', fontWeight: 'bold' },

  table: { width: '100%', borderWidth: 1, borderColor: SKY[200], borderRadius: 8, marginTop: 6, marginBottom: 10, overflow: 'hidden' },
  headerRow: { flexDirection: 'row', backgroundColor: SKY[100], borderBottomWidth: 1, borderBottomColor: SKY[200] },
  cellHeader: {
    paddingVertical: 7, paddingHorizontal: 6, fontSize: 9, fontWeight: 'bold', color: SKY[800],
    borderRightWidth: 1, borderRightColor: SKY[200],
  },
  row: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: SKY[100], alignItems: 'center' },
  rowAlt: { backgroundColor: SKY[50] },
  cell: {
    paddingVertical: 6, paddingHorizontal: 6, fontSize: 9, color: '#333',
    borderRightWidth: 1, borderRightColor: SKY[100],
  },
  shareCell: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 6 },
  barTrack: { flexGrow: 1, height: 6, backgroundColor: '#e5e7eb', borderRadius: 3, marginRight: 6 },
  barFill: { height: 6, backgroundColor: BAR, borderRadius: 3 },
  pct: { width: 34, fontSize: 8.5, color: '#555', textAlign: 'right' },

  totalRow: { flexDirection: 'row', backgroundColor: SKY[100] },
  totalText: { fontSize: 9.5, fontWeight: 'bold', color: SKY[900] },

  emptyState: { marginTop: 40, textAlign: 'center', fontSize: 12, color: '#999', fontStyle: 'italic' },
});

const formatTitleDate = (date) => {
  const d = date ? new Date(date) : new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
};

const formatGeneratedAt = (date) =>
  (date ? new Date(date) : new Date()).toLocaleString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });

const NaturesCountReport = ({ reportData = [], totalCount = 0, generatedAt, qrCodeUrl, rangeLabel = 'All time' }) => {
  const rows = Array.isArray(reportData) ? reportData : [];
  const maxCount = Math.max(...rows.map((r) => r.count || 0), 1);

  return (
    <Document
      title={`Natures Count Report ${formatTitleDate(generatedAt)}`}
      author="Forest & Wildlife Department, Punjab"
      subject="Natures Count Report"
    >
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.headerBlock} fixed>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 0 }}>
            <Image src={process.env.PUBLIC_URL + '/assets/images/logo_fw.png'} style={{ width: 130, height: 'auto' }} />
            <View style={{ flex: 0.5, alignItems: 'flex-end', justifyContent: 'flex-start' }}>
              {qrCodeUrl && <Image src={qrCodeUrl} style={{ width: 60, height: 60 }} />}
              <Text style={{ fontSize: 8, fontWeight: 'bold', marginTop: 2 }}>Natures Count Report</Text>
              <Text style={{ fontSize: 8, textAlign: 'right', marginBottom: 0, marginTop: 5, fontWeight: 'bold' }}>
                Generated Date: {formatGeneratedAt(generatedAt)}
              </Text>
            </View>
          </View>
          <View style={styles.dividerTop} />
        </View>

        <Text style={styles.heading}>Natures Count Report</Text>
        <Text style={styles.subHeading}>
          Period: {rangeLabel} · {totalCount.toLocaleString()} complaint{totalCount === 1 ? '' : 's'} · non-informative only
        </Text>

        {rows.length === 0 ? (
          <Text style={styles.emptyState}>No complaints found for the selected period.</Text>
        ) : (
          <View style={styles.table}>
            <View style={styles.headerRow} fixed>
              <Text style={[styles.cellHeader, { width: COL.index }]}>#</Text>
              <Text style={[styles.cellHeader, { width: COL.name }]}>Complaint Nature</Text>
              <Text style={[styles.cellHeader, { width: COL.count, textAlign: 'right' }]}>Count</Text>
              <Text style={[styles.cellHeader, { width: COL.share, borderRightWidth: 0 }]}>Share</Text>
            </View>

            {rows.map((row, index) => {
              const pct = totalCount ? (row.count / totalCount) * 100 : 0;
              return (
                <View key={`${row.natureId || row.name}-${index}`} style={[styles.row, index % 2 === 1 && styles.rowAlt]} wrap={false}>
                  <Text style={[styles.cell, { width: COL.index }]}>{index + 1}</Text>
                  <Text style={[styles.cell, { width: COL.name }]}>{row.name || 'Unknown Nature'}</Text>
                  <Text style={[styles.cell, { width: COL.count, textAlign: 'right' }]}>{(row.count || 0).toLocaleString()}</Text>
                  <View style={[styles.shareCell, { width: COL.share }]}>
                    <View style={styles.barTrack}>
                      <View style={[styles.barFill, { width: `${((row.count || 0) / maxCount) * 100}%` }]} />
                    </View>
                    <Text style={styles.pct}>{pct.toFixed(1)}%</Text>
                  </View>
                </View>
              );
            })}

            <View style={styles.totalRow} wrap={false}>
              <Text style={[styles.cell, styles.totalText, { width: `${parseFloat(COL.index) + parseFloat(COL.name)}%` }]}>Total</Text>
              <Text style={[styles.cell, styles.totalText, { width: COL.count, textAlign: 'right' }]}>{totalCount.toLocaleString()}</Text>
              <Text style={[styles.cell, styles.totalText, { width: COL.share, borderRightWidth: 0, textAlign: 'right' }]}>100%</Text>
            </View>
          </View>
        )}

        <View
          style={{ position: 'absolute', bottom: 10, left: 20, right: 20, flexDirection: 'column', alignItems: 'center' }}
          fixed
        >
          <View style={{ width: '100%', height: 1, backgroundColor: 'black', marginBottom: 5, marginTop: 10 }} />
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', width: '100%', fontSize: 6 }}>
            <Text>F&W Department, Punjab, Pakistan</Text>
            <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default NaturesCountReport;
